import React, { useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { db } from '@root/firebase';
import {
  addDoc,
  collection,
  doc as fsDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';

/**
 * ReadMoreText
 *
 * Props:
 * - text: string (HTML allowed; will be sanitized)
 * - wordLimit: number
 * - readMore: boolean (show the toggle)
 * - docPath: string | undefined  -> Firestore doc path to increment, e.g. "adverts/abc123"
 * - userId: string | undefined   -> current user id to save in the view doc
 * - onViewed?: () => void        -> optional callback after view is recorded
 * - toggleWordInactive           -> change the text to show
 * - toggleWordActive             -> change the text to show
 * - linkTo                       -> calls MarketplaceView.jsx to show product or advert page
 * - marketplaceId                -> product or advert Id to show
 */

function ReadMoreText({
  text = '',
  wordLimit = 20,
  readMore = true,
  docPath,
  userId,
  onViewed,
  plainText = false,
  toggleWordInactive = 'Read More',
  toggleWordActive = 'Read less',
  linkTo = false,
  marketplaceId,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasRecordedViewRef = useRef(false);

  // Sanitize once
  const sanitizedText = useMemo(() => DOMPurify.sanitize(text || ''), [text]);

  // Build a safe truncated preview
  const { truncatedHTML, shouldShowReadMore } = useMemo(() => {
    const temp =
      typeof document !== 'undefined' ? document.createElement('div') : null;
    let plain = '';
    if (temp) {
      temp.innerHTML = sanitizedText;
      plain = temp.textContent || temp.innerText || '';
    } else {
      plain = sanitizedText.replace(/<[^>]+>/g, ''); // server-safe fallback
    }
    const words = plain.trim().length ? plain.trim().split(/\s+/) : [];
    const needsMore = words.length > wordLimit;
    const preview = words.slice(0, wordLimit).join(' ');
    return {
      shouldShowReadMore: needsMore,
      truncatedHTML: DOMPurify.sanitize(preview),
    };
  }, [sanitizedText, wordLimit]);

  // Firestore writer: add a view + increment totalViews on parent
  const recordView = async () => {
    if (!db || !docPath || !userId) return;

    // 1) Add view doc under `${docPath}/views`
    const viewsCol = collection(db, `${docPath}/views`);
    await addDoc(viewsCol, {
      userId,
      createdAt: serverTimestamp(),
    });

    // 2) Increment `totalViews` on the parent doc
    const parentRef = fsDoc(db, docPath);
    await updateDoc(parentRef, {
      totalViews: increment(1),
    });
  };

  const handleToggle = async () => {
    // When opening for the first time, record a view
    if (!isExpanded && docPath && userId) {
      try {
        await recordView();
        hasRecordedViewRef.current = true;
        onViewed && onViewed();
        console.warn('[ReadMoreText] recordView Success', userId, docPath);
      } catch (e) {
        // keep UX smooth even if logging fails
        console.warn('[ReadMoreText] recordView failed', e);
      }
    }
    setIsExpanded((v) => !v);
  };

  return plainText ? (
    <span className='text-muted'>
      {isExpanded ? sanitizedText : `${truncatedHTML}...`}
    </span>
  ) : (
    <p className='text-muted'>
      <span
        dangerouslySetInnerHTML={{
          __html: isExpanded ? sanitizedText : `${truncatedHTML}...`,
        }}
      />
      {readMore && shouldShowReadMore && (
        <>
          {' '}
          <span
            className='primary-hover'
            style={{
              fontWeight: 'bold',
              cursor: 'pointer',
              color: isExpanded ? 'red' : undefined,
            }}
            onClick={handleToggle}
            title={isExpanded ? toggleWordActive : toggleWordInactive}
          >
            {isExpanded ? toggleWordActive : toggleWordInactive}
          </span>
        </>
      )}
      <br />
    </p>
  );
}

export default ReadMoreText;
